import api from './api'
import type { JournalEntryDraft } from './accounting'

export type ExpenseType = 'depreciation' | 'amortization'
export type AssetStatus = 'active' | 'disposed'
export type MonthUiStatus = 'complete' | 'partial' | 'incomplete'
export type MonthJeStatus = 'none' | 'draft' | 'posted' | 'stale'
export type SyncIssueSeverity = 'error' | 'warning' | 'info'
export type SyncIssueType =
  | 'asset_account_missing'
  | 'asset_account_renamed'
  | 'cost_drift'
  | 'accum_drift'
  | 'unmapped_account'

export type FixedAssetFlag = { key: string; label: string }

export type FixedAssetListItem = {
  id: number
  name: string
  asset_class: string
  location: string
  description: string | null
  purchase_price: string
  salvage_value: string
  purchase_date: string
  dep_start_date: string
  method: string
  useful_life_years: number
  non_depreciable: boolean
  expense_type: ExpenseType
  asset_account_qbo_id: string
  expense_account_qbo_id: string
  accumulated_account_qbo_id: string
  status: AssetStatus
  accum_to_date: string
  nbv: string
  monthly_run_rate: string
  pct_depreciated: string
  flags: FixedAssetFlag[]
}

export type FixedAssetWriteBody = {
  name: string
  asset_class: string
  location: string
  description?: string | null
  purchase_price: string | number
  salvage_value: string | number
  purchase_date: string
  dep_start_date: string
  method?: string
  useful_life_years: number
  non_depreciable?: boolean
  expense_type: ExpenseType
  asset_account_qbo_id: string
  expense_account_qbo_id: string
  accumulated_account_qbo_id: string
}

export type FixedAssetResponse = FixedAssetWriteBody & {
  id: number
  status: AssetStatus
  method: string
  non_depreciable: boolean
}

export type AssetBreakdownRow = {
  asset_id: number
  name: string
  expense_type: ExpenseType
  location: string
  expense: string
  true_up: string
  accum_through: string
  nbv: string
  amount?: string
}

export type SyncIssue = {
  type: SyncIssueType
  severity: SyncIssueSeverity
  qbo_account_id: string
  name: string
  message: string
  asset_id?: number | null
  delta?: string | null
}

export type SyncCheckResponse = {
  id: number
  ran_at: string
  ok: boolean
  issues: SyncIssue[]
  account_snapshot: Record<string, { name: string; balance: string; type: string; active?: boolean }>
  error_count: number
  warning_count: number
}

export type MonthSummaryItem = {
  report_month: string
  status: MonthUiStatus
  hint: string | null
  je_status: MonthJeStatus
  content_dirty: boolean
  needs_qbo_update: boolean
  doc_number: string
}

export type MonthDetailResponse = {
  report_month: string
  status: MonthJeStatus
  ui_status: MonthUiStatus
  hint: string | null
  content_dirty: boolean
  needs_qbo_update: boolean
  doc_number: string
  draft: JournalEntryDraft | null
  rebuilt_payload: Record<string, unknown> | null
  asset_breakdown: AssetBreakdownRow[]
  latest_sync: { ran_at: string | null; ok: boolean; error_count: number; warning_count: number }
}

export type GenerateDraftResponse = {
  zero_expense: boolean
  draft: JournalEntryDraft | null
  payload: Record<string, unknown>
  overwritten: boolean
}

export type PushQboUpdateResponse = {
  draft: JournalEntryDraft
  regenerated: boolean
}

export type QboAccountOption = {
  id: string
  name: string
  fully_qualified_name: string
  account_type: string
  balance: string
  already_linked: boolean
}

export type ScheduleMonthItem = {
  year: number
  month: number
  elapsed_months: number
  expense: string
  expected_accum: string
  nbv: string
}

export type FixedAssetScheduleResponse = {
  asset_id: number
  name: string
  purchase_price: string
  salvage_value: string
  dep_start_date: string
  useful_life_years: number
  non_depreciable: boolean
  months: ScheduleMonthItem[]
}

export async function listFixedAssets(): Promise<{ assets: FixedAssetListItem[] }> {
  const response = await api.get<{ assets: FixedAssetListItem[] }>('/fixed-assets/assets')
  return response.data
}

export async function createFixedAsset(body: FixedAssetWriteBody): Promise<FixedAssetResponse> {
  const response = await api.post<FixedAssetResponse>('/fixed-assets/assets', body)
  return response.data
}

export async function updateFixedAsset(
  id: number,
  body: FixedAssetWriteBody,
): Promise<FixedAssetResponse> {
  const response = await api.put<FixedAssetResponse>(`/fixed-assets/assets/${id}`, body)
  return response.data
}

export async function deleteFixedAsset(id: number): Promise<void> {
  await api.delete(`/fixed-assets/assets/${id}`)
}

export async function getFixedAssetSchedule(id: number): Promise<FixedAssetScheduleResponse> {
  const response = await api.get<FixedAssetScheduleResponse>(`/fixed-assets/assets/${id}/schedule`)
  return response.data
}

export async function getFixedAssetMonthsSummary(): Promise<{ months: MonthSummaryItem[] }> {
  const response = await api.get<{ months: MonthSummaryItem[] }>('/fixed-assets/months-summary')
  return response.data
}

export async function getFixedAssetMonth(reportMonth: string): Promise<MonthDetailResponse> {
  const response = await api.get<MonthDetailResponse>(`/fixed-assets/months/${reportMonth}`)
  return response.data
}

export async function generateFixedAssetDraft(
  reportMonth: string,
  opts?: { overwrite_posted?: boolean },
): Promise<GenerateDraftResponse> {
  const response = await api.post<GenerateDraftResponse>(
    `/fixed-assets/months/${reportMonth}/draft`,
    { overwrite_posted: opts?.overwrite_posted ?? false },
  )
  return response.data
}

export async function pushFixedAssetDraftQboUpdate(
  draftId: number,
  opts?: { force_regenerate?: boolean },
): Promise<PushQboUpdateResponse> {
  const response = await api.post<PushQboUpdateResponse>(
    `/fixed-assets/drafts/${draftId}/push-qbo-update`,
    { force_regenerate: opts?.force_regenerate ?? false },
  )
  return response.data
}

export async function getFixedAssetSyncCheck(opts?: { refresh?: boolean }): Promise<SyncCheckResponse> {
  const response = await api.get<SyncCheckResponse>('/fixed-assets/sync-check', {
    params: opts?.refresh ? { refresh: true } : undefined,
  })
  return response.data
}

export type FixedAssetNavStatusResponse = {
  id: number
  ran_at: string
  allocate_incomplete_count: number
}

export async function getFixedAssetNavStatus(
  opts?: { refresh?: boolean },
): Promise<FixedAssetNavStatusResponse> {
  const response = await api.get<FixedAssetNavStatusResponse>('/fixed-assets/nav-status', {
    params: opts?.refresh ? { refresh: true } : undefined,
  })
  return response.data
}

export async function getIgnoredAccounts(): Promise<{ account_ids: string[] }> {
  const response = await api.get<{ account_ids: string[] }>('/fixed-assets/ignored-accounts')
  return response.data
}

export async function updateIgnoredAccounts(accountIds: string[]): Promise<{ account_ids: string[] }> {
  const response = await api.put<{ account_ids: string[] }>('/fixed-assets/ignored-accounts', {
    account_ids: accountIds,
  })
  return response.data
}

export async function listFixedAssetQboAccounts(): Promise<QboAccountOption[]> {
  const response = await api.get<QboAccountOption[]>('/fixed-assets/qbo-accounts')
  return response.data
}

export type FixedAssetsSchedulesSheetExportRequest = {
  title?: string
  headers: string[]
  rows: string[][]
}

export type FixedAssetsSchedulesSheetExportResponse = {
  spreadsheet_id: string
  url: string
  name: string
}

export async function exportFixedAssetsSchedulesSheet(
  payload: FixedAssetsSchedulesSheetExportRequest,
): Promise<FixedAssetsSchedulesSheetExportResponse> {
  const { data } = await api.post<FixedAssetsSchedulesSheetExportResponse>(
    '/fixed-assets/assets/export-sheet',
    payload,
  )
  return data
}
